import { Combobox, Quantity } from "@/components";
import { searchFormulaVersions, searchMaterials, type FormulaVersionOption, type MaterialOption } from "../api";
import styles from "./Pickers.module.css";

interface PickerProps<T> {
  label: string;
  hideLabel?: boolean;
  value: T | null;
  onValueChange: (value: T | null) => void;
  error?: string | null;
  description?: string;
  optional?: boolean;
  size?: "sm" | "md";
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/** Material search by name, trade name, short code, synonym or CAS, with what's on the shelf. */
export function MaterialCombobox(props: PickerProps<MaterialOption>) {
  return (
    <Combobox<MaterialOption>
      {...props}
      placeholder={props.placeholder ?? "Name, trade name or CAS…"}
      getOptionLabel={(option) => option.name}
      getOptionKey={(option) => option.id}
      renderOption={(option) => (
        <span className={styles.option}>
          <span className={styles.name}>{option.name}</span>
          <span className={styles.detail}>
            {option.cas ? (
              <span className={styles.cas} translate="no">
                {option.cas}
              </span>
            ) : null}
            {option.owned_g ? (
              <span>
                <Quantity value={option.owned_g} unit="g" /> on the shelf
              </span>
            ) : null}
            <span>
              {option.listing_count
                ? `${option.listing_count} supplier listing${option.listing_count === 1 ? "" : "s"}`
                : "No supplier listing yet"}
            </span>
          </span>
        </span>
      )}
      loadOptions={searchMaterials}
      queryKey={["purchasing", "material-search"]}
    />
  );
}

export function versionLabel(option: { code: string; label: string; name: string }): string {
  return `${option.code} ${option.label}: ${option.name}`;
}

/** Formula versions of this workspace by code or name. */
export function FormulaVersionCombobox(props: PickerProps<FormulaVersionOption>) {
  return (
    <Combobox<FormulaVersionOption>
      {...props}
      placeholder={props.placeholder ?? "Formula code or name…"}
      getOptionLabel={versionLabel}
      getOptionKey={(option) => option.id}
      renderOption={(option) => (
        <span className={styles.option}>
          <span className={styles.name}>
            <span className={styles.code}>{option.code}</span> {option.label}
          </span>
          <span className={styles.detail}>
            <span>{option.name}</span>
            <span>{option.status === "draft" ? "Draft" : option.status === "locked" ? "Locked" : "Released"}</span>
          </span>
        </span>
      )}
      loadOptions={searchFormulaVersions}
      minQueryLength={0}
      queryKey={["purchasing", "formula-version-search"]}
    />
  );
}
